/**
 * WS4 Playback modal — full 4-tab session viewer ported from the legacy
 * `#playbackModal` shell.
 *
 * Ported from:
 *   - `_legacy/ws4-assetlocker.js` lines 534-791 — `#playbackModal`
 *     shell, the `.dt-tab-bar` with four tabs (Overview / Key Moments
 *     / Timeline / Clips), and the per-pane content (verbatim
 *     cell-for-cell).
 *   - `_legacy/ws4-assetlocker.js` lines 559-615 — the inline
 *     `<style>` block; ported to `src/index.css` under `.pb-*`.
 *
 * Visual conventions match the other WS4 modals: `PanelDialogContent`
 * shell, true-blue border + halo, brushed-grey outline, 12 px radius;
 * 3-col title bar (left identity / centre cluster / right ✕ Close);
 * legacy `dt-tab-bar` swap; footer pinned to the bottom.
 *
 * Functional notes:
 *   - Clicking a chapter row sets `activeChapterId` (decorative
 *     scroll/jump — no real playback wiring).
 *   - Clicking the big play button or the centre control toggles
 *     `playing` and swaps the icon (▶ → ⏸).
 *   - Clicking a Key Moment or a Clip switches to the Overview tab and
 *     marks the corresponding chapter active (decorative jump).
 *   - Timeline segments are decorative no-ops.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { useTranscripts, type Transcript } from "@/stores/useTranscripts";
import type { Workshop } from "@/content/workshops";
import { MediaTranscriptPane } from "@/features/ws4-transcript/MediaTranscriptPane";
import { parseTranscriptBody } from "@/features/ws4-transcript/transcript-cues";
import { derivePlayback } from "./derive-playback";
import {
  resolveSessionDate,
  withSessionDate,
} from "@/features/workshop/session-date";
import {
  Captions,
  ChevronsRight,
  ChevronsLeft,
  Maximize2,
  Pause,
  PlayCircle,
  Play,
  X,
} from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import {
  getPlayback,
  type ChapterAccent,
  type Clip,
  type ClipAccent,
  type KeyMoment,
  type KeyMomentAccent,
  type PlaybackChapter,
  type PlaybackStat,
  type SpeakerAccent,
  type SpeakerRow,
  type TimelineAccent,
  type TimelineSegment,
} from "./playback-content";

type PlaybackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

type TabKey = "overview" | "highlights" | "timeline" | "clips";

/** Stable empty reference — avoids a new array each render (selector identity). */
const EMPTY_TRANSCRIPTS: Transcript[] = [];

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "highlights", label: "Key Moments" },
  { key: "timeline", label: "Timeline" },
  { key: "clips", label: "Clips" },
];

/* ------------------------- Accent → token ------------------------- */

/** Chapter row → `--accent-*-rgb` for the thumbnail radial-gradient tint. */
const CHAPTER_RGB: Record<ChapterAccent, string> = {
  "true-blue": "var(--accent-true-blue-rgb)",
  gold: "var(--accent-gold-rgb)",
  sky: "var(--accent-sky-rgb)",
  melon: "var(--accent-melon-rgb)",
  red: "var(--accent-danger-rgb)", // legacy danger red — routed via --accent-danger
  green: "var(--accent-green-rgb)",
};

/** Chapter row → `--accent-*` for the play icon stroke. */
const CHAPTER_VAR: Record<ChapterAccent, string> = {
  "true-blue": "var(--accent-true-blue)",
  gold: "var(--accent-gold)",
  sky: "var(--accent-sky)",
  melon: "var(--accent-melon)",
  red: "var(--accent-danger)",
  green: "var(--accent-green)",
};

/** Key Moment card → icon-square + tag-pill tint. */
const KM_TINT: Record<KeyMomentAccent, { rgb: string; color: string }> = {
  sky: { rgb: "var(--accent-sky-rgb)", color: "var(--accent-sky)" },
  green: { rgb: "var(--accent-green-rgb)", color: "var(--accent-green)" },
  red: { rgb: "var(--accent-danger-rgb)", color: "var(--accent-danger)" },
  "true-blue": {
    rgb: "var(--accent-true-blue-rgb)",
    color: "var(--accent-true-blue)",
  },
  gold: { rgb: "var(--accent-gold-rgb)", color: "var(--accent-gold)" },
  melon: { rgb: "var(--accent-melon-rgb)", color: "var(--accent-melon)" },
};

/** Speaker row → coloured text. */
const SPEAKER_COLOR: Record<SpeakerAccent, string> = {
  "true-blue": "var(--accent-true-blue)",
  sky: "var(--accent-sky)",
  green: "var(--accent-green)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
  pink: "#FF8FAB",
  muted: "var(--text-muted)",
};

/** Clip tag colour. */
const CLIP_TAG_COLOR: Record<ClipAccent, string> = {
  green: "var(--accent-green)",
  "true-blue": "var(--accent-true-blue)",
  red: "var(--accent-danger)",
  gold: "var(--accent-gold)",
  melon: "var(--accent-melon)",
};

/** Clip thumb radial-gradient tint (rgb tuple). */
const CLIP_THUMB_RGB: Record<ClipAccent, string> = {
  green: "var(--accent-green-rgb)",
  "true-blue": "var(--accent-true-blue-rgb)",
  red: "var(--accent-danger-rgb)",
  gold: "var(--accent-gold-rgb)",
  melon: "var(--accent-melon-rgb)",
};

/** Timeline segment accent → background + text colour. */
const TL_STYLE: Record<TimelineAccent, { bg: string; color: string }> = {
  "true-blue": {
    bg: "rgba(var(--accent-true-blue-rgb), 0.15)",
    color: "var(--accent-true-blue)",
  },
  gold: {
    bg: "rgba(var(--accent-gold-rgb), 0.12)",
    color: "var(--accent-gold)",
  },
  sky: {
    bg: "rgba(var(--accent-sky-rgb), 0.10)",
    color: "var(--accent-sky)",
  },
  "gold-break": {
    bg: "rgba(var(--accent-gold-rgb), 0.06)",
    color: "var(--accent-gold)",
  },
  "red-pink": {
    bg: "rgba(var(--accent-danger-rgb), 0.10)",
    color: "#FF8FAB",
  },
  green: {
    bg: "rgba(var(--accent-green-rgb), 0.10)",
    color: "var(--accent-green)",
  },
  melon: {
    bg: "rgba(var(--accent-melon-rgb), 0.15)",
    color: "var(--accent-melon)",
  },
  "sky-fill": {
    bg: "rgba(var(--accent-sky-rgb), 0.30)",
    color: "transparent",
  },
  "true-blue-fill": {
    bg: "rgba(var(--accent-true-blue-rgb), 0.25)",
    color: "var(--accent-true-blue)",
  },
};

/* --------------------------- Component --------------------------- */

export function PlaybackModal({
  open,
  onOpenChange,
  workshopId,
}: PlaybackModalProps) {
  // Real uploaded recordings (transcripts with attached media) take
  // precedence over the authored demo fixture — UAT: "upload the
  // transcript and media, it doesn't update in the Outputs".
  const transcripts = useTranscripts(
    (s) =>
      s.entriesByWorkshop[workshopId as Workshop["id"]] ?? EMPTY_TRANSCRIPTS,
  );
  const recordings = useMemo(
    () => transcripts.filter((t) => t.mediaBlobKey || t.mediaPath),
    [transcripts],
  );
  const [activeRecordingId, setActiveRecordingId] = useState<string | null>(
    null,
  );
  // Hydrate when opened — the modal is reachable from surfaces that don't
  // otherwise mount the Repository view (e.g. the Asset Locker card).
  useEffect(() => {
    if (open) void useTranscripts.getState().hydrate();
  }, [open]);

  const authored = getPlayback(workshopId);
  // Session date — swap the frozen seed date in the authored subtitle /
  // timeline strings for the (override-aware) agenda date (UAT: live dates).
  const override = useEffectiveWorkshopOverride(workshopId);
  const sessionDate = resolveSessionDate(workshopId, override);
  const data = useMemo(() => {
    if (!authored || !sessionDate) return authored;
    return {
      ...authored,
      sessionSubtitle: withSessionDate(authored.sessionSubtitle, sessionDate),
      timelineTitle: withSessionDate(authored.timelineTitle, sessionDate),
    };
  }, [authored, sessionDate]);
  const firstChapterId = data?.chapters[0]?.id ?? null;

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [activeChapterId, setActiveChapterId] = useState<string | null>(
    firstChapterId,
  );
  const [playing, setPlaying] = useState(false);

  /** Switch to Overview + mark a chapter active. Used by Key Moment + Clip clicks. */
  const jumpToMinute = (minutes: number) => {
    // Decorative — pick the chapter whose start time is the closest
    // (and not exceeding) the target minute.
    if (!data) return;
    let chosen: PlaybackChapter | null = null;
    for (const c of data.chapters) {
      if (c.jumpMinutes <= minutes) {
        chosen = c;
      }
    }
    if (chosen) {
      setActiveChapterId(chosen.id);
    }
    setActiveTab("overview");
  };

  // `find ?? [0]` narrows to undefined only when there are no recordings
  // at all — that case falls through to the authored fixture below.
  const activeRecording =
    recordings.find((r) => r.id === activeRecordingId) ?? recordings[0];
  if (activeRecording) {
    return (
      // Keyed by recording so tab/chapter/player state resets cleanly
      // when the facilitator switches recordings.
      <RecordedPlayback
        key={activeRecording.id}
        open={open}
        onOpenChange={onOpenChange}
        workshopId={workshopId}
        recordings={recordings}
        active={activeRecording}
        onSelectRecording={setActiveRecordingId}
      />
    );
  }

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <PanelDialogContent
          className="max-w-5xl border-0 bg-transparent p-0 shadow-none"
          style={{ outline: "none" }}
        >
          <DialogTitle className="sr-only">Playback</DialogTitle>
          <DialogDescription className="sr-only">
            No playback registered for workshop {workshopId}.
          </DialogDescription>
          <div
            className="rounded-xl p-8 text-center"
            style={{
              background: "var(--bg-panel-alt)",
              border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
              outline: "3px solid #7892A9",
              outlineOffset: 0,
              color: "var(--text-muted)",
            }}
          >
            No playback registered for workshop {workshopId}.
          </div>
        </PanelDialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "88vw",
          maxWidth: 1020,
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
        }}
      >
        <DialogTitle className="sr-only">
          Playback — {data.sessionTitle}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {data.sessionSubtitle}. {data.footerSummary}.
        </DialogDescription>

        <div
          className="modal-shell"
          style={{
            width: "100%",
            maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
            outline: "3px solid #7892A9",
            outlineOffset: 0,
          }}
        >
          {/* ── Header ─────────────────────────────────────────────── */}
          <div className="modal-header">
            <div className="flex items-center gap-[10px]">
              <PlayCircle
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">PLAYBACK</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER
              </span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                WORKSHOP 4 — HUMAN-AGENT WAYS OF WORKING
              </span>
            </div>
            <div className="flex items-center gap-[6px]">
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                Session Recording
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* ── Tab bar ───────────────────────────────────────────── */}
          <div
            className="dt-tab-bar"
            role="tablist"
            aria-label="Playback sections"
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                className={cn("dt-tab", activeTab === t.key && "dt-tab-active")}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Body ──────────────────────────────────────────────── */}
          <div className="modal-body" style={{ padding: 0 }}>
            {activeTab === "overview" && (
              <OverviewPane
                stats={data.stats}
                chapters={data.chapters}
                activeChapterId={activeChapterId}
                onSelectChapter={(id) => setActiveChapterId(id)}
                playing={playing}
                onTogglePlay={() => setPlaying((p) => !p)}
                sessionTitle={data.sessionTitle}
                sessionSubtitle={data.sessionSubtitle}
                durationBadge={data.durationBadge}
                scrubberLabel={data.scrubberLabel}
                scrubberMarkers={data.scrubberChapterMarkers}
              />
            )}
            {activeTab === "highlights" && (
              <HighlightsPane
                keyMoments={data.keyMoments}
                onJump={jumpToMinute}
              />
            )}
            {activeTab === "timeline" && (
              <TimelinePane
                title={data.timelineTitle}
                ticks={data.timelineTicks}
                segments={data.timelineSegments}
                speakingTime={data.speakingTime}
              />
            )}
            {activeTab === "clips" && (
              <ClipsPane clips={data.clips} onJump={jumpToMinute} />
            )}
          </div>

          {/* ── Footer ────────────────────────────────────────────── */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Asset Locker — Playback</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: "var(--accent-sky)" }}
            >
              Workshop 4 — Human-Agent Ways of Working
            </span>
            <span className="modal-footer-right">{data.footerSummary}</span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/* =================================================================== */
/* RECORDED PLAYBACK                                                   */
/* =================================================================== */

/**
 * The uploaded-recording experience: the same four derived tabs the
 * authored fixture gets (Overview / Key Moments / Timeline / Clips —
 * all computed from the transcript cues, see `derive-playback.ts`)
 * plus a Recording tab hosting the real synced player. The player
 * stays MOUNTED across tabs (display toggle) so chapter / key-moment /
 * clip clicks can seek the live media element and audio keeps playing
 * while the facilitator browses the breakdown.
 *
 * Unsynced transcripts (no per-cue timestamps) can't derive the
 * time-based surfaces — they render the plain player view only.
 */
function RecordedPlayback({
  open,
  onOpenChange,
  workshopId,
  recordings,
  active,
  onSelectRecording,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
  recordings: Transcript[];
  active: Transcript;
  onSelectRecording: (id: string) => void;
}) {
  const parsed = useMemo(() => parseTranscriptBody(active.body), [active.body]);
  const derived = useMemo(
    () => derivePlayback(active, parsed, `XAES Phase Zero · ${workshopId}`),
    [active, parsed, workshopId],
  );

  type RecTabKey = TabKey | "recording";
  const tabs: ReadonlyArray<{ key: RecTabKey; label: string }> = derived
    ? [...TABS, { key: "recording", label: "Recording" }]
    : [{ key: "recording", label: "Recording" }];
  const [activeTab, setActiveTab] = useState<RecTabKey>(
    derived ? "overview" : "recording",
  );
  const [activeChapterId, setActiveChapterId] = useState<string | null>(
    derived?.chapters[0]?.id ?? null,
  );
  const [playing, setPlaying] = useState(false);

  // The live media element, surfaced by MediaTranscriptPane. Play/pause
  // listeners keep the Overview play glyph honest; attached per element
  // (the old element's listeners die with it).
  const mediaElRef = useRef<HTMLMediaElement | null>(null);
  const onMediaEl = useCallback((el: HTMLMediaElement | null) => {
    mediaElRef.current = el;
    if (!el) return;
    const sync = () => setPlaying(!el.paused);
    el.addEventListener("play", sync);
    el.addEventListener("pause", sync);
    sync();
  }, []);

  /** Jump the REAL recording to a minute mark and show the player. */
  const seekMinutes = (minutes: number) => {
    setActiveTab("recording");
    const el = mediaElRef.current;
    if (!el) return;
    el.currentTime = minutes * 60;
    const p = el.play?.();
    if (p) void p.catch(() => {});
  };

  const seekChapter = (id: string) => {
    setActiveChapterId(id);
    const bound = derived?.chapterBounds.find((c) => c.id === id);
    if (bound) seekMinutes(bound.startMs / 60_000);
  };

  const togglePlay = () => {
    const el = mediaElRef.current;
    if (!el) {
      setActiveTab("recording");
      return;
    }
    if (el.paused) {
      const p = el.play();
      if (p) void p.catch(() => {});
    } else {
      el.pause();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{
          outline: "none",
          width: "88vw",
          maxWidth: 1020,
          maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
        }}
      >
        <DialogTitle className="sr-only">
          Playback — recorded session
        </DialogTitle>
        <DialogDescription className="sr-only">
          Uploaded session recordings for workshop {workshopId}.
        </DialogDescription>

        <div
          className="modal-shell"
          style={{
            width: "100%",
            maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
            outline: "3px solid #7892A9",
            outlineOffset: 0,
          }}
        >
          {/* ── Header ─────────────────────────────────────────── */}
          <div className="modal-header">
            <div className="flex items-center gap-[10px]">
              <PlayCircle
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">PLAYBACK</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                RECORDED SESSION
              </span>
            </div>
            <div className="flex items-center gap-[6px]">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* ── Recording selector — only when there's a choice ── */}
          {recordings.length > 1 && (
            <div
              className="dt-tab-bar"
              role="tablist"
              aria-label="Uploaded recordings"
            >
              {recordings.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active.id === r.id}
                  onClick={() => onSelectRecording(r.id)}
                  className={cn("dt-tab", active.id === r.id && "dt-tab-active")}
                >
                  {r.title}
                </button>
              ))}
            </div>
          )}

          {/* ── Section tab bar ──────────────────────────────────── */}
          <div className="dt-tab-bar" role="tablist" aria-label="Playback sections">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                className={cn("dt-tab", activeTab === t.key && "dt-tab-active")}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Body ─────────────────────────────────────────────── */}
          <div
            className="modal-body"
            style={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {derived && activeTab !== "recording" && (
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {activeTab === "overview" && (
                  <OverviewPane
                    stats={derived.stats}
                    chapters={derived.chapters}
                    activeChapterId={activeChapterId}
                    onSelectChapter={seekChapter}
                    playing={playing}
                    onTogglePlay={togglePlay}
                    sessionTitle={derived.sessionTitle}
                    sessionSubtitle={derived.sessionSubtitle}
                    durationBadge={derived.durationBadge}
                    scrubberLabel={derived.scrubberLabel}
                    scrubberMarkers={derived.scrubberChapterMarkers}
                  />
                )}
                {activeTab === "highlights" && (
                  <HighlightsPane
                    keyMoments={derived.keyMoments}
                    onJump={seekMinutes}
                  />
                )}
                {activeTab === "timeline" && (
                  <TimelinePane
                    title={derived.timelineTitle}
                    ticks={derived.timelineTicks}
                    segments={derived.timelineSegments}
                    speakingTime={derived.speakingTime}
                  />
                )}
                {activeTab === "clips" && (
                  <ClipsPane clips={derived.clips} onJump={seekMinutes} />
                )}
              </div>
            )}
            {/* Player — kept mounted so seeks land and audio survives
                tab switches; hidden rather than unmounted. */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: activeTab === "recording" ? "flex" : "none",
                flexDirection: "column",
              }}
            >
              <MediaTranscriptPane transcript={active} onMediaEl={onMediaEl} />
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────── */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Asset Locker — Playback</span>
            {derived && (
              <span className="modal-footer-right">{derived.footerSummary}</span>
            )}
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/* =================================================================== */
/* OVERVIEW PANE                                                       */
/* =================================================================== */

function OverviewPane({
  stats,
  chapters,
  activeChapterId,
  onSelectChapter,
  playing,
  onTogglePlay,
  sessionTitle,
  sessionSubtitle,
  durationBadge,
  scrubberLabel,
  scrubberMarkers,
}: {
  stats: ReadonlyArray<PlaybackStat>;
  chapters: ReadonlyArray<PlaybackChapter>;
  activeChapterId: string | null;
  onSelectChapter: (id: string) => void;
  playing: boolean;
  onTogglePlay: () => void;
  sessionTitle: string;
  sessionSubtitle: string;
  durationBadge: string;
  scrubberLabel: string;
  scrubberMarkers: ReadonlyArray<{ left: number; title: string }>;
}) {
  const PlayPauseIcon = playing ? Pause : Play;
  return (
    <div className="pb-pane">
      {/* Player */}
      <div className="pb-player" style={{ marginBottom: 16 }}>
        <div className="pb-screen">
          <div className="pb-screen-bg" aria-hidden />
          <div className="pb-live-badge">
            <span
              aria-hidden
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "var(--accent-danger)",
                display: "inline-block",
              }}
            />
            REC
          </div>
          <div className="pb-duration-badge">{durationBadge}</div>
          {/* Hero title from props — the authored WS4 fixture passes its
              session name, derived recordings pass the upload's title.
              (Was hardcoded to the legacy two-line WS4 string.) */}
          <div className="pb-session-title">{sessionTitle}</div>
          <div className="pb-session-sub">{sessionSubtitle}</div>
          <button
            type="button"
            className="pb-play-btn"
            onClick={onTogglePlay}
            aria-label={playing ? "Pause session recording" : "Play session recording"}
            aria-hidden={false}
            title={sessionTitle}
          >
            <PlayPauseIcon
              width={22}
              height={22}
              style={{ color: "var(--accent-sky)" }}
              aria-hidden
            />
          </button>
        </div>
        <div className="pb-controls">
          <button
            type="button"
            className="pb-ctrl-btn"
            aria-label="Skip back 30 seconds"
            title="−30s"
          >
            <ChevronsLeft style={{ width: 12, height: 12 }} aria-hidden />
          </button>
          <button
            type="button"
            className="pb-ctrl-btn"
            aria-label={playing ? "Pause" : "Play"}
            onClick={onTogglePlay}
          >
            <PlayPauseIcon style={{ width: 12, height: 12 }} aria-hidden />
          </button>
          <button
            type="button"
            className="pb-ctrl-btn"
            aria-label="Skip forward 30 seconds"
            title="+30s"
          >
            <ChevronsRight style={{ width: 12, height: 12 }} aria-hidden />
          </button>
          <div className="pb-progress-wrap">
            {scrubberMarkers.map((m) => (
              <div
                key={m.title}
                className="pb-chapter-marker"
                style={{ left: `${m.left}%` }}
                title={m.title}
                aria-hidden
              />
            ))}
            <div
              className="pb-progress-fill"
              style={{ width: "0%" }}
              aria-hidden
            />
            <div
              className="pb-progress-thumb"
              style={{ left: "0%" }}
              aria-hidden
            />
          </div>
          <span className="pb-time">{scrubberLabel}</span>
          <button
            type="button"
            className="pb-speed-btn"
            aria-label="Playback speed"
          >
            1×
          </button>
          <button
            type="button"
            className="pb-ctrl-btn"
            aria-label="Captions"
            title="Captions"
          >
            <Captions style={{ width: 11, height: 11, opacity: 0.5 }} aria-hidden />
          </button>
          <button
            type="button"
            className="pb-ctrl-btn"
            aria-label="Full screen"
            title="Full screen"
          >
            <Maximize2 style={{ width: 11, height: 11 }} aria-hidden />
          </button>
        </div>
      </div>

      {/* Stat grid */}
      <div className="pb-stat-grid">
        {stats.map((s) => (
          <div key={s.label} className="pb-stat-card">
            <div className="pb-stat-label">{s.label}</div>
            <div
              className="pb-stat-value"
              style={{ color: STAT_COLOR[s.accent] }}
            >
              {s.value}
            </div>
            <div className="pb-stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Chapters */}
      <div className="dt-section-title">Chapters</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {chapters.map((c) => (
          <ChapterRow
            key={c.id}
            chapter={c}
            active={activeChapterId === c.id}
            onSelect={() => onSelectChapter(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

const STAT_COLOR: Record<PlaybackStat["accent"], string> = {
  sky: "var(--accent-sky)",
  green: "var(--accent-green)",
  "true-blue": "var(--accent-true-blue)",
  gold: "var(--accent-gold)",
};

function ChapterRow({
  chapter,
  active,
  onSelect,
}: {
  chapter: PlaybackChapter;
  active: boolean;
  onSelect: () => void;
}) {
  const rgb = CHAPTER_RGB[chapter.accent];
  const accentVar = CHAPTER_VAR[chapter.accent];

  // Per-chapter duration colour — Break is gold, Activity 2.3 is red,
  // Close is green, others are muted. Mirrors the legacy inline colour.
  const durationColor = chapter.warning
    ? "var(--accent-danger)"
    : chapter.isBreak
      ? "var(--accent-gold)"
      : chapter.accent === "green"
        ? "var(--accent-green)"
        : "var(--text-muted)";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn("pb-chapter-row", active && "pb-chapter-active")}
    >
      <div className="pb-chapter-thumb">
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(circle at 50% 50%, rgba(${rgb}, 0.15), transparent 70%)`,
          }}
        />
        <Play
          aria-hidden
          style={{ width: 16, height: 16, color: accentVar, opacity: 0.5 }}
        />
      </div>
      <span className="pb-chapter-ts">{chapter.timestamp}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="pb-chapter-title">{chapter.title}</div>
        <div className="pb-chapter-desc">{chapter.description}</div>
      </div>
      <span
        style={{
          fontSize: "var(--fs-micro)",
          color: durationColor,
          fontFamily: "var(--font-mono)",
        }}
      >
        {chapter.duration}
        {chapter.warning ? " ⚠" : ""}
      </span>
    </button>
  );
}

/* =================================================================== */
/* HIGHLIGHTS PANE                                                     */
/* =================================================================== */

function HighlightsPane({
  keyMoments,
  onJump,
}: {
  keyMoments: ReadonlyArray<KeyMoment>;
  onJump: (minutes: number) => void;
}) {
  return (
    <div className="pb-pane">
      <div className="dt-section-title">
        Auto-Tagged Key Moments — XAES-AI
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {keyMoments.map((km) => {
          const tint = KM_TINT[km.accent];
          return (
            <button
              key={km.id}
              type="button"
              className="pb-hl-card"
              onClick={() => onJump(km.jumpMinutes)}
            >
              <span className="pb-hl-ts">{km.timestamp}</span>
              <div
                className="pb-hl-icon"
                aria-hidden
                style={{ background: `rgba(${tint.rgb}, 0.10)` }}
              >
                {km.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="pb-hl-title">{km.title}</div>
                <div className="pb-hl-desc">{km.description}</div>
                <span
                  className="pb-hl-tag"
                  style={{
                    background: `rgba(${tint.rgb}, 0.08)`,
                    border: `1px solid rgba(${tint.rgb}, 0.25)`,
                    color: tint.color,
                  }}
                >
                  {km.tagLabel}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* =================================================================== */
/* TIMELINE PANE                                                       */
/* =================================================================== */

function TimelinePane({
  title,
  ticks,
  segments,
  speakingTime,
}: {
  title: string;
  ticks: ReadonlyArray<string>;
  segments: ReadonlyArray<TimelineSegment>;
  speakingTime: ReadonlyArray<SpeakerRow>;
}) {
  const flow = segments.filter((s) => s.track === "flow");
  const speaking = segments.filter((s) => s.track === "speaking");
  const events = segments.filter((s) => s.track === "events");

  return (
    <div className="pb-pane">
      <div className="dt-section-title">{title}</div>
      <div className="pb-tl-ruler">
        {ticks.map((t) => (
          <span key={t} className="pb-tl-tick">
            {t}
          </span>
        ))}
      </div>

      <TrackLabel>Session Flow</TrackLabel>
      <div className="pb-timeline-track" style={{ marginBottom: 12 }}>
        {flow.map((s) => (
          <TimelineSegmentEl key={s.id} segment={s} />
        ))}
      </div>

      <TrackLabel>Speaking Time</TrackLabel>
      <div
        className="pb-timeline-track"
        style={{ marginBottom: 12, height: 36 }}
      >
        {speaking.map((s) => (
          <TimelineSegmentEl key={s.id} segment={s} thinTrack />
        ))}
      </div>

      <TrackLabel>Key Events</TrackLabel>
      <div className="pb-timeline-track" style={{ height: 36, marginBottom: 16 }}>
        {events.map((s) => (
          <TimelineSegmentEl key={s.id} segment={s} thinTrack />
        ))}
      </div>

      <div className="dt-section-title">Speaking Time Breakdown</div>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Speaker</th>
            <th>Role</th>
            <th>Est. Speaking Time</th>
            <th>% of Session</th>
            <th>Key Contribution</th>
          </tr>
        </thead>
        <tbody>
          {speakingTime.map((row) => {
            const color = SPEAKER_COLOR[row.accent];
            const isMuted = row.accent === "muted";
            return (
              <tr key={row.speaker}>
                <td
                  style={{
                    color,
                    fontWeight: isMuted ? 400 : 600,
                  }}
                >
                  {row.speaker}
                </td>
                <td>{row.role}</td>
                <td>{row.estTime}</td>
                <td style={{ color }}>{row.percent}</td>
                <td>{row.contribution}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TrackLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: "var(--fs-micro)",
        color: "var(--text-muted)",
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: 5,
      }}
    >
      {children}
    </div>
  );
}

function TimelineSegmentEl({
  segment,
  thinTrack,
}: {
  segment: TimelineSegment;
  thinTrack?: boolean;
}) {
  const style = TL_STYLE[segment.accent];
  const css: CSSProperties = {
    left: `${segment.left}%`,
    width: `${segment.width}%`,
    background: style.bg,
    color: style.color,
    ...(thinTrack ? { top: 2, height: 30, fontSize: "var(--fs-micro)" } : null),
    ...(segment.italic ? { fontStyle: "italic" } : null),
  };
  return (
    <button
      type="button"
      className="pb-tl-segment"
      style={css}
      aria-label={segment.label || "Timeline segment"}
      title={segment.label || undefined}
    >
      {segment.label}
    </button>
  );
}

/* =================================================================== */
/* CLIPS PANE                                                          */
/* =================================================================== */

function ClipsPane({
  clips,
  onJump,
}: {
  clips: ReadonlyArray<Clip>;
  onJump: (minutes: number) => void;
}) {
  return (
    <div className="pb-pane">
      <div className="dt-section-title">
        Saved Clips — Auto-generated by XAES-AI
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}
      >
        {clips.map((clip) => {
          const rgb = CLIP_THUMB_RGB[clip.tagAccent];
          const tagColor = CLIP_TAG_COLOR[clip.tagAccent];
          return (
            <button
              key={clip.id}
              type="button"
              className="pb-clip-card"
              onClick={() => onJump(clip.jumpMinutes)}
            >
              <div className="pb-clip-thumb">
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: `radial-gradient(ellipse at ${clip.thumbGradientX}% ${clip.thumbGradientY}%, rgba(${rgb}, 0.08), transparent 70%)`,
                  }}
                />
                <div className="pb-clip-play" aria-hidden>
                  <Play
                    style={{
                      width: 14,
                      height: 14,
                      color: "var(--accent-sky)",
                      marginLeft: 2,
                    }}
                    aria-hidden
                  />
                </div>
                <span className="pb-clip-dur">{clip.duration}</span>
              </div>
              <div className="pb-clip-body">
                <div className="pb-clip-title">{clip.title}</div>
                <div className="pb-clip-meta">
                  {clip.timestamp} · {clip.speaker} ·{" "}
                  <span style={{ color: tagColor }}>{clip.tagLabel}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
